/**
 * AYU Rezepte — Personalisierte ayurvedische Rezepte
 * + Sammlungen (Ordner) zum Organisieren
 * + Rezepte in der Community teilen
 * + PDF-Export
 * + Eigene Rezepte manuell hinzufügen / bearbeiten / aus Foto importieren
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  UtensilsCrossed,
  Heart,
  Trash2,
  Loader2,
  Sparkles,
  Clock,
  Leaf,
  ChefHat,
  Coffee,
  Sun,
  Moon,
  Cookie,
  GlassWater,
  Plus,
  X,
  PenLine,
  Pencil,
  Camera,
  ImagePlus,
  Check,
  FolderPlus,
  Folder,
  FolderOpen,
  Share2,
  FileDown,
  MoreHorizontal,
  Users,
} from "lucide-react";
import { ThumbsFeedback } from "@/components/app/ThumbsFeedback";
import { useState, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";

interface Ingredient {
  name: string;
  amount: string;
  unit: string;
}

interface InstructionStep {
  step: number;
  text: string;
}

const MEAL_TYPES = [
  { value: "fruehstueck" as const, labelKey: "rezepte.fruehstueck", icon: <Coffee className="w-4 h-4" /> },
  { value: "mittag" as const, labelKey: "rezepte.mittagessen", icon: <Sun className="w-4 h-4" /> },
  { value: "abend" as const, labelKey: "rezepte.abendessen", icon: <Moon className="w-4 h-4" /> },
  { value: "snack" as const, labelKey: "rezepte.snack", icon: <Cookie className="w-4 h-4" /> },
  { value: "getraenk" as const, labelKey: "rezepte.getraenk", icon: <GlassWater className="w-4 h-4" /> },
];

const MEAL_LABELS: Record<string, string> = {
  fruehstueck: "Frühstück",
  mittag: "Mittagessen",
  abend: "Abendessen",
  snack: "Snack",
  getraenk: "Getränk",
};

type MealType = "fruehstueck" | "mittag" | "abend" | "snack" | "getraenk";

const COLLECTION_EMOJIS = ["📁", "🍲", "🥗", "🍵", "🌿", "🔥", "💧", "🌙", "☀️", "❄️", "🌸", "🍯", "🥣", "🫖", "🌶️"];

const inputClass = "w-full px-3 py-2 rounded-lg border border-[#d4dcc4] bg-[#f8f8f8] text-sm text-[#2d3a1e] placeholder:text-[#8a9680] focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30";

/* ── Helper: Zutaten zu Text ── */
function ingredientsToText(ingredients: Ingredient[]): string {
  return ingredients
    .map((ing) => {
      const parts = [ing.amount, ing.unit, ing.name].filter(Boolean);
      return parts.join(" ");
    })
    .join("\n");
}

/* ── Helper: Schritte zu Text ── */
function instructionsToText(instructions: InstructionStep[]): string {
  return instructions.map((s) => s.text).join("\n");
}

/* ── Helper: Rezept als formatierten Text ── */
function recipeToText(recipe: {
  title: string;
  description: string | null;
  mealType: string;
  ingredients: unknown;
  instructions: unknown;
  prepTimeMinutes: number | null;
  ayurvedicNote: string | null;
}): string {
  const ingredients = (recipe.ingredients as Ingredient[]) ?? [];
  const instructions = (recipe.instructions as InstructionStep[]) ?? [];
  let text = `🍽️ ${recipe.title}\n`;
  if (recipe.description) text += `${recipe.description}\n`;
  text += `\nKategorie: ${MEAL_LABELS[recipe.mealType] ?? recipe.mealType}`;
  if (recipe.prepTimeMinutes) text += ` | ⏱️ ${recipe.prepTimeMinutes} Min`;
  text += `\n\n📋 Zutaten:\n`;
  ingredients.forEach((ing) => {
    text += `• ${[ing.amount, ing.unit, ing.name].filter(Boolean).join(" ")}\n`;
  });
  text += `\n👨‍🍳 Zubereitung:\n`;
  instructions.forEach((step, i) => {
    text += `${i + 1}. ${step.text}\n`;
  });
  if (recipe.ayurvedicNote) {
    text += `\n🌿 Ayurvedischer Hinweis: ${recipe.ayurvedicNote}`;
  }
  return text;
}

/* ── Helper: PDF generieren ── */
function generateRecipePDF(recipe: {
  title: string;
  description: string | null;
  mealType: string;
  ingredients: unknown;
  instructions: unknown;
  prepTimeMinutes: number | null;
  ayurvedicNote: string | null;
}) {
  const ingredients = (recipe.ingredients as Ingredient[]) ?? [];
  const instructions = (recipe.instructions as InstructionStep[]) ?? [];

  // Build HTML for PDF
  const html = `
    <!DOCTYPE html>
    <html><head><meta charset="utf-8">
    <style>
      body { font-family: 'Georgia', serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #2d3a1e; }
      h1 { color: #2d3a1e; border-bottom: 2px solid #6b7f4e; padding-bottom: 8px; }
      .meta { color: #5a6b4a; font-size: 14px; margin-bottom: 20px; }
      h2 { color: #6b7f4e; font-size: 18px; margin-top: 24px; }
      ul { padding-left: 20px; }
      li { margin-bottom: 4px; color: #5a6b4a; }
      ol li { margin-bottom: 8px; line-height: 1.5; }
      .note { background: #f0f4e8; border-left: 3px solid #6b7f4e; padding: 12px; margin-top: 20px; font-style: italic; color: #5a6b4a; }
      .footer { margin-top: 40px; text-align: center; color: #8a9680; font-size: 12px; }
    </style></head><body>
    <h1>${recipe.title}</h1>
    ${recipe.description ? `<p class="meta">${recipe.description}</p>` : ""}
    <p class="meta">${MEAL_LABELS[recipe.mealType] ?? recipe.mealType}${recipe.prepTimeMinutes ? ` · ${recipe.prepTimeMinutes} Minuten` : ""}</p>
    <h2>Zutaten</h2>
    <ul>${ingredients.map((ing) => `<li>${[ing.amount, ing.unit, ing.name].filter(Boolean).join(" ")}</li>`).join("")}</ul>
    <h2>Zubereitung</h2>
    <ol>${instructions.map((step) => `<li>${step.text}</li>`).join("")}</ol>
    ${recipe.ayurvedicNote ? `<div class="note">🌿 ${recipe.ayurvedicNote}</div>` : ""}
    <div class="footer">Erstellt mit AYU — Dein persönlicher Gesundheitsplan</div>
    </body></html>
  `;

  // Open in new window for printing
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  }
}

/* ══════════════════════════════════════════════════════════════
   CollectionsSidebar — Sammlungen verwalten
   ══════════════════════════════════════════════════════════════ */

function CollectionsSidebar({
  clientId,
  activeCollectionId,
  onSelectCollection,
  collections,
  refetchCollections,
}: {
  clientId: string;
  activeCollectionId: number | null;
  onSelectCollection: (id: number | null) => void;
  collections: { id: number; name: string; emoji: string | null; color: string | null }[];
  refetchCollections: () => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmoji, setNewEmoji] = useState("📁");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  const { t } = useI18n();

  const createMutation = trpc.recipeCollections.create.useMutation({
    onSuccess: () => {
      toast.success(t("rezepte.sammlungErstellt"));
      setShowCreate(false);
      setNewName("");
      refetchCollections();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.recipeCollections.update.useMutation({
    onSuccess: () => {
      toast.success(t("rezepte.sammlungUmbenannt"));
      setEditingId(null);
      refetchCollections();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.recipeCollections.delete.useMutation({
    onSuccess: () => {
      toast.success(t("rezepte.sammlungGeloescht"));
      if (activeCollectionId === editingId) onSelectCollection(null);
      setEditingId(null);
      refetchCollections();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-[#5a6b4a] uppercase tracking-wider">{t("rezepte.sammlungen")}</h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="p-1 rounded hover:bg-[#eef2e6] transition-colors"
          title={t("rezepte.neueSammlung")}
        >
          <FolderPlus className="w-4 h-4 text-[#6b7f4e]" />
        </button>
      </div>

      {/* Alle Rezepte */}
      <button
        onClick={() => onSelectCollection(null)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
          activeCollectionId === null
            ? "bg-[#6b7f4e] text-white"
            : "text-[#5a6b4a] hover:bg-[#eef2e6]"
        }`}
      >
        <ChefHat className="w-4 h-4" />
        {t("rezepte.alle")}
      </button>

      {/* Favoriten */}
      <button
        onClick={() => onSelectCollection(-1)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
          activeCollectionId === -1
            ? "bg-[#6b7f4e] text-white"
            : "text-[#5a6b4a] hover:bg-[#eef2e6]"
        }`}
      >
        <Heart className="w-4 h-4" />
        {t("rezepte.favoriten")}
      </button>

      {/* Collections */}
      {collections.map((col) => (
        <div key={col.id} className="group relative">
          {editingId === col.id ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="flex-1 px-2 py-1 text-xs rounded border border-[#d4dcc4] bg-white"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editName.trim()) {
                    updateMutation.mutate({ id: col.id, clientId, name: editName.trim() });
                  }
                  if (e.key === "Escape") setEditingId(null);
                }}
              />
              <button
                onClick={() => {
                  if (editName.trim()) updateMutation.mutate({ id: col.id, clientId, name: editName.trim() });
                }}
                className="p-1 text-[#6b7f4e] hover:bg-[#eef2e6] rounded"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  if (confirm(t("rezepte.sammlungLoeschenBestaetigung"))) {
                    deleteMutation.mutate({ id: col.id, clientId });
                  }
                }}
                className="p-1 text-red-400 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => onSelectCollection(col.id)}
              onDoubleClick={() => {
                setEditingId(col.id);
                setEditName(col.name);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                activeCollectionId === col.id
                  ? "bg-[#6b7f4e] text-white"
                  : "text-[#5a6b4a] hover:bg-[#eef2e6]"
              }`}
            >
              <span>{col.emoji || "📁"}</span>
              <span className="truncate flex-1 text-left">{col.name}</span>
              <Pencil
                className="w-3 h-3 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(col.id);
                  setEditName(col.name);
                }}
              />
            </button>
          )}
        </div>
      ))}

      {/* Create Form */}
      {showCreate && (
        <div className="p-2 bg-white/80 rounded-lg border border-[#d4dcc4] space-y-2">
          <div className="flex gap-1 flex-wrap">
            {COLLECTION_EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => setNewEmoji(e)}
                className={`w-7 h-7 rounded text-sm flex items-center justify-center transition-all ${
                  newEmoji === e ? "bg-[#6b7f4e]/20 ring-1 ring-[#6b7f4e]" : "hover:bg-[#eef2e6]"
                }`}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("rezepte.sammelnNamePlaceholder")}
            className={inputClass + " text-xs"}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) {
                createMutation.mutate({ clientId, name: newName.trim(), emoji: newEmoji });
              }
            }}
          />
          <div className="flex gap-1">
            <Button
              size="sm"
              onClick={() => {
                if (newName.trim()) createMutation.mutate({ clientId, name: newName.trim(), emoji: newEmoji });
              }}
              disabled={!newName.trim() || createMutation.isPending}
              className="flex-1 bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white text-xs h-7"
            >
              {createMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t("rezepte.erstellen")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowCreate(false); setNewName(""); }}
              className="text-xs h-7 border-[#d4dcc4]"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   AddToCollectionMenu — Rezept zu Sammlung hinzufügen/entfernen
   ══════════════════════════════════════════════════════════════ */

function AddToCollectionMenu({
  recipeId,
  collections,
  onClose,
}: {
  recipeId: number;
  collections: { id: number; name: string; emoji: string | null }[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { data: recipeCollectionIds, refetch } = trpc.recipeCollections.getRecipeCollections.useQuery(
    { recipeId },
  );

  const addMutation = trpc.recipeCollections.addRecipe.useMutation({
    onSuccess: () => refetch(),
  });
  const removeMutation = trpc.recipeCollections.removeRecipe.useMutation({
    onSuccess: () => refetch(),
  });

  const isInCollection = (colId: number) => recipeCollectionIds?.includes(colId) ?? false;

  return (
    <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-[#d4dcc4] p-2 min-w-[180px]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-[#5a6b4a]">{t("rezepte.sammlungen")}</span>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-[#eef2e6]">
          <X className="w-3 h-3 text-[#8a9680]" />
        </button>
      </div>
      {collections.length === 0 ? (
        <p className="text-xs text-[#8a9680] py-2 text-center">{t("rezepte.nochKeinNachricht")}</p>
      ) : (
        <div className="space-y-1">
          {collections.map((col) => {
            const inCol = isInCollection(col.id);
            return (
              <button
                key={col.id}
                onClick={() => {
                  if (inCol) {
                    removeMutation.mutate({ collectionId: col.id, recipeId });
                  } else {
                    addMutation.mutate({ collectionId: col.id, recipeId });
                  }
                }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-all ${
                  inCol ? "bg-[#6b7f4e]/10 text-[#6b7f4e]" : "text-[#5a6b4a] hover:bg-[#eef2e6]"
                }`}
              >
                <span>{col.emoji || "📁"}</span>
                <span className="flex-1 text-left truncate">{col.name}</span>
                {inCol && <Check className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   RecipeCard — Einzelne Rezept-Karte
   ══════════════════════════════════════════════════════════════ */

function RecipeCard({
  recipe,
  onToggleFavorite,
  onDelete,
  onEdit,
  clientId,
  collections,
  onShare,
}: {
  recipe: {
    id: number;
    title: string;
    description: string | null;
    mealType: string;
    ingredients: unknown;
    instructions: unknown;
    prepTimeMinutes: number | null;
    ayurvedicNote: string | null;
    suitableFor: string | null;
    imageUrl: string | null;
    isFavorite: boolean;
    createdAt: Date;
  };
  onToggleFavorite: () => void;
  onDelete: () => void;
  onEdit: () => void;
  clientId?: string;
  collections: { id: number; name: string; emoji: string | null }[];
  onShare: () => void;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [showCollectionMenu, setShowCollectionMenu] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const ingredients = (recipe.ingredients as Ingredient[]) ?? [];
  const instructions = (recipe.instructions as InstructionStep[]) ?? [];

  return (
    <Card className="bg-white/80 backdrop-blur-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Rezeptbild */}
      {recipe.imageUrl && (
        <div
          className="w-full h-40 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <img
            src={recipe.imageUrl}
            alt={recipe.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#eef2e6] text-[#5a6b4a] border border-[#d4dcc4]">
                {MEAL_LABELS[recipe.mealType] ?? recipe.mealType}
              </span>
              {recipe.prepTimeMinutes && (
                <span className="text-xs text-[#8a9680] flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {recipe.prepTimeMinutes} Min
                </span>
              )}
            </div>
            <CardTitle
              className="text-base text-[#2d3a1e] cursor-pointer hover:text-[#6b7f4e] transition-colors"
              style={{ fontFamily: "'Playfair Display', serif" }}
              onClick={() => setExpanded(!expanded)}
            >
              {recipe.title}
            </CardTitle>
            {recipe.description && (
              <p className="text-xs text-[#5a6b4a] mt-1">{recipe.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg hover:bg-[#eef2e6] transition-colors"
              title={t("rezepte.bearbeiten")}
            >
              <Pencil className="w-4 h-4 text-[#8a9680] hover:text-[#6b7f4e]" />
            </button>
            <button
              onClick={onToggleFavorite}
              className="p-1.5 rounded-lg hover:bg-[#eef2e6] transition-colors"
            >
              <Heart
                className={`w-4 h-4 ${recipe.isFavorite ? "fill-red-400 text-red-400" : "text-[#8a9680]"}`}
              />
            </button>
            {/* More actions */}
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-1.5 rounded-lg hover:bg-[#eef2e6] transition-colors"
              >
                <MoreHorizontal className="w-4 h-4 text-[#8a9680]" />
              </button>
              {showActions && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-[#d4dcc4] p-1 min-w-[160px]">
                  <button
                    onClick={() => { setShowCollectionMenu(true); setShowActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-[#5a6b4a] hover:bg-[#eef2e6] transition-colors"
                  >
                    <Folder className="w-3.5 h-3.5" />
                    {t("rezepte.sammlung")}
                  </button>
                  <button
                    onClick={() => { onShare(); setShowActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-[#5a6b4a] hover:bg-[#eef2e6] transition-colors"
                  >
                    <Users className="w-3.5 h-3.5" />
                    {t("rezepte.teilen")}
                  </button>
                  <button
                    onClick={() => { generateRecipePDF(recipe); setShowActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-[#5a6b4a] hover:bg-[#eef2e6] transition-colors"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    {t("rezepte.pdfDrucken")}
                  </button>
                  <div className="border-t border-[#eef2e6] my-1" />
                  <button
                    onClick={() => { onDelete(); setShowActions(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t("rezepte.loeschen")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Collection menu popover */}
        {showCollectionMenu && (
          <div className="relative">
            <AddToCollectionMenu
              recipeId={recipe.id}
              collections={collections}
              onClose={() => setShowCollectionMenu(false)}
            />
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-4">
          {/* Zutaten */}
          <div>
            <h4 className="text-sm font-medium text-[#2d3a1e] mb-2 flex items-center gap-1">
              <UtensilsCrossed className="w-3.5 h-3.5 text-[#6b7f4e]" />
              {t("rezepte.zutaten")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {ingredients.map((ing, i) => (
                <div key={i} className="text-xs text-[#5a6b4a] py-0.5">
                  <span className="font-medium">{ing.amount} {ing.unit}</span> {ing.name}
                </div>
              ))}
            </div>
          </div>

          {/* Zubereitung */}
          <div>
            <h4 className="text-sm font-medium text-[#2d3a1e] mb-2 flex items-center gap-1">
              <ChefHat className="w-3.5 h-3.5 text-[#6b7f4e]" />
              {t("rezepte.zubereitung")}
            </h4>
            <div className="space-y-2">
              {instructions.map((step, i) => (
                <div key={i} className="flex gap-3 text-xs">
                  <span className="w-5 h-5 rounded-full bg-[#6b7f4e]/10 text-[#6b7f4e] flex items-center justify-center font-medium shrink-0">
                    {step.step || i + 1}
                  </span>
                  <p className="text-[#5a6b4a] leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Ayurvedischer Hinweis */}
          {recipe.ayurvedicNote && (
            <div className="p-3 rounded-lg bg-[#8ba668]/10 border border-[#8ba668]/20">
              <div className="flex items-center gap-2 mb-1">
                <Leaf className="w-3.5 h-3.5 text-[#8ba668]" />
                <span className="text-xs font-medium text-[#5a6b4a]">{t("rezepte.ayurvHinweis")}</span>
              </div>
              <p className="text-xs text-[#5a6b4a] leading-relaxed">{recipe.ayurvedicNote}</p>
            </div>
          )}

          {/* Quick actions */}
          <div className="flex items-center gap-2 pt-1 border-t border-[#e8e0d4]">
            <button
              onClick={onShare}
              className="flex items-center gap-1 text-[10px] text-[#8a9680] hover:text-[#6b7f4e] transition-colors"
            >
              <Share2 className="w-3 h-3" />
              {t("rezepte.teilenKurz")}
            </button>
            <button
              onClick={() => generateRecipePDF(recipe)}
              className="flex items-center gap-1 text-[10px] text-[#8a9680] hover:text-[#6b7f4e] transition-colors"
            >
              <FileDown className="w-3 h-3" />
              PDF
            </button>
            {clientId && (
              <div className="ml-auto">
                <ThumbsFeedback
                  clientId={clientId}
                  sessionType="rezept"
                  sessionRef={recipe.id.toString()}
                  context="/app/rezepte"
                  variant="minimal"
                />
              </div>
            )}
          </div>
        </CardContent>
      )}

      {!expanded && (
        <CardContent className="pt-0">
          <button
            onClick={() => setExpanded(true)}
            className="text-xs text-[#6b7f4e] hover:underline"
          >
            {t("rezepte.rezeptAnzeigen")}
          </button>
        </CardContent>
      )}
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════
   EditRecipeForm — Rezept bearbeiten (vorausgefüllt)
   ══════════════════════════════════════════════════════════════ */

function EditRecipeForm({
  recipe,
  clientId,
  onSuccess,
  onCancel,
}: {
  recipe: {
    id: number;
    title: string;
    description: string | null;
    mealType: string;
    ingredients: unknown;
    instructions: unknown;
    prepTimeMinutes: number | null;
    ayurvedicNote: string | null;
  };
  clientId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const ingredients = (recipe.ingredients as Ingredient[]) ?? [];
  const instructions = (recipe.instructions as InstructionStep[]) ?? [];

  const [title, setTitle] = useState(recipe.title);
  const [description, setDescription] = useState(recipe.description || "");
  const [mealType, setMealType] = useState<MealType>((recipe.mealType as MealType) || "mittag");
  const [ingredientsText, setIngredientsText] = useState(ingredientsToText(ingredients));
  const [instructionsText, setInstructionsText] = useState(instructionsToText(instructions));
  const [prepTime, setPrepTime] = useState(recipe.prepTimeMinutes?.toString() || "");
  const [note, setNote] = useState(recipe.ayurvedicNote || "");

  const { t } = useI18n();

  const updateMutation = trpc.recipes.update.useMutation({
    onSuccess: () => {
      toast.success(t("rezepte.rezeptAktualisiert"));
      onSuccess();
    },
    onError: (err) => toast.error("Fehler: " + err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error(t("rezepte.titelEingeben")); return; }
    updateMutation.mutate({
      recipeId: recipe.id,
      clientId,
      title: title.trim(),
      description: description.trim() || undefined,
      mealType,
      ingredients: ingredientsText.trim() || undefined,
      instructions: instructionsText.trim() || undefined,
      prepTimeMinutes: prepTime ? parseInt(prepTime) : undefined,
      ayurvedicNote: note.trim() || undefined,
    });
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-[#6b7f4e]/30 border-2">
      <CardContent className="py-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-[#2d3a1e] flex items-center gap-2">
            <Pencil className="w-4 h-4 text-[#6b7f4e]" />
            {t("rezepte.bearbeiten")}
          </h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-[#eef2e6] transition-colors">
            <X className="w-4 h-4 text-[#8a9680]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.titel")} <span className="text-red-400">*</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} required />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.kurzbeschreibung")}</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1.5 block">{t("rezepte.kategorie")}</label>
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((mt) => (
                <button key={mt.value} type="button" onClick={() => setMealType(mt.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${mealType === mt.value ? "bg-[#6b7f4e] text-white" : "bg-[#eef2e6] text-[#5a6b4a] hover:bg-[#6b7f4e]/20"}`}>
                  {mt.icon}{t(mt.labelKey as any)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zutaten")} <span className="text-[#8a9680] font-normal">{t("rezepte.eineProZeile")}</span></label>
            <textarea value={ingredientsText} onChange={(e) => setIngredientsText(e.target.value)} rows={4} className={inputClass + " resize-none"} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zubereitung")} <span className="text-[#8a9680] font-normal">{t("rezepte.einSchritt")}</span></label>
            <textarea value={instructionsText} onChange={(e) => setInstructionsText(e.target.value)} rows={4} className={inputClass + " resize-none"} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zubereitungszeit")}</label>
              <div className="relative">
                <input type="number" value={prepTime} onChange={(e) => setPrepTime(e.target.value)} placeholder="z.B. 30" min="1" max="300" className={inputClass + " pr-12"} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a9680]">{t("rezepte.min")}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.notizHinweis")}</label>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={updateMutation.isPending || !title.trim()} className="flex-1 bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white">
              {updateMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("rezepte.wirdGespeichert")}</> : <><Check className="w-4 h-4 mr-2" />{t("rezepte.aenderungenSpeichern")}</>}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="text-[#5a6b4a] border-[#d4dcc4]">{t("rezepte.abbrechen")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════
   AddManualRecipeForm — Eigenes Rezept manuell hinzufügen
   ══════════════════════════════════════════════════════════════ */

function AddManualRecipeForm({ clientId, onSuccess, onCancel }: { clientId: string; onSuccess: () => void; onCancel: () => void; }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mealType, setMealType] = useState<MealType>("mittag");
  const [ingredients, setIngredients] = useState("");
  const [instructions, setInstructions] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [note, setNote] = useState("");

  const { t } = useI18n();

  const addManual = trpc.recipes.addManual.useMutation({
    onSuccess: (data) => { toast.success(t("rezepte.rezeptGespeichert", { title: data.title })); onSuccess(); },
    onError: (err) => toast.error("Fehler: " + err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error(t("rezepte.titelEingeben")); return; }
    addManual.mutate({ clientId, title: title.trim(), description: description.trim() || undefined, mealType, ingredients: ingredients.trim() || undefined, instructions: instructions.trim() || undefined, prepTimeMinutes: prepTime ? parseInt(prepTime) : undefined, ayurvedicNote: note.trim() || undefined });
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-[#6b7f4e]/30 border-2">
      <CardContent className="py-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-[#2d3a1e] flex items-center gap-2"><PenLine className="w-4 h-4 text-[#6b7f4e]" />{t("rezepte.eigenesHinzufuegen")}</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-[#eef2e6] transition-colors"><X className="w-4 h-4 text-[#8a9680]" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.titel")} <span className="text-red-400">*</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. Goldene Milch, Kitchari, Ingwer-Tee..." className={inputClass} required />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.kurzbeschreibung")}</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Worum geht es bei diesem Rezept?" className={inputClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1.5 block">{t("rezepte.kategorie")}</label>
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((mt) => (
                <button key={mt.value} type="button" onClick={() => setMealType(mt.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${mealType === mt.value ? "bg-[#6b7f4e] text-white" : "bg-[#eef2e6] text-[#5a6b4a] hover:bg-[#6b7f4e]/20"}`}>
                  {mt.icon}{t(mt.labelKey as any)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zutaten")} <span className="text-[#8a9680] font-normal">{t("rezepte.eineProZeile")}</span></label>
            <textarea value={ingredients} onChange={(e) => setIngredients(e.target.value)} placeholder={"200g Basmatireis\n1 EL Ghee\n1 TL Kurkuma\n1 Prise Salz"} rows={4} className={inputClass + " resize-none"} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zubereitung")} <span className="text-[#8a9680] font-normal">{t("rezepte.einSchritt")}</span></label>
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={"Reis waschen und 30 Min einweichen\nGhee in einem Topf erhitzen\nGewürze anbraten\nReis hinzufügen und 20 Min köcheln"} rows={4} className={inputClass + " resize-none"} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.zubereitungszeit")}</label>
              <div className="relative">
                <input type="number" value={prepTime} onChange={(e) => setPrepTime(e.target.value)} placeholder="z.B. 30" min="1" max="300" className={inputClass + " pr-12"} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a9680]">{t("rezepte.min")}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5a6b4a] mb-1 block">{t("rezepte.notizHinweis")}</label>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="z.B. Vata-beruhigend" className={inputClass} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={addManual.isPending || !title.trim()} className="flex-1 bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white">
              {addManual.isPending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("rezepte.wirdGespeichert")}</> : <><Plus className="w-4 h-4 mr-2" />{t("rezepte.speichern")}</>}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="text-[#5a6b4a] border-[#d4dcc4]">{t("rezepte.abbrechen")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════
   PhotoImportCard — Rezept aus Foto importieren
   ══════════════════════════════════════════════════════════════ */

function PhotoImportCard({ clientId, onSuccess }: { clientId: string; onSuccess: () => void; }) {
  const [isImporting, setIsImporting] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useI18n();

  const importMutation = trpc.recipes.importFromPhoto.useMutation({
    onSuccess: (data) => { toast.success(t("rezepte.rezeptFotoImport", { title: String(data.title) })); setPreview(null); setIsImporting(false); onSuccess(); },
    onError: (err) => { toast.error("Fehler: " + err.message); setIsImporting(false); },
  });

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error(t("rezepte.bildTypFehler")); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(t("rezepte.bildZuGross")); return; }
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
      const base64 = dataUrl.split(",")[1];
      importMutation.mutate({ clientId, imageBase64: base64, mimeType: file.type as "image/jpeg" | "image/png" | "image/webp" });
    };
    reader.onerror = () => { toast.error(t("rezepte.fehlerBildLesen")); setIsImporting(false); };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, [clientId, importMutation, t]);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-[#d4dcc4] hover:shadow-md transition-shadow">
      <CardContent className="py-4 space-y-3">
        <h3 className="text-sm font-medium text-[#2d3a1e] flex items-center gap-2"><Camera className="w-4 h-4 text-[#6b7f4e]" />{t("rezepte.rezeptAusФoto")}</h3>
        {preview && isImporting ? (
          <div className="relative rounded-lg overflow-hidden">
            <img src={preview} alt="Rezept-Foto" className="w-full h-40 object-cover opacity-70" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="flex items-center gap-2 bg-white/90 px-4 py-2 rounded-full">
                <Loader2 className="w-4 h-4 animate-spin text-[#6b7f4e]" />
                <span className="text-xs text-[#2d3a1e]">{t("rezepte.rezeptWirdErkannt")}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#8a9680]">{t("rezepte.fotoDesc")}</p>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" capture="environment" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={isImporting} variant="outline" className="w-full text-[#6b7f4e] border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/10">
              <ImagePlus className="w-4 h-4 mr-2" />{t("rezepte.fotoAuswaehlen")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════
   AppRezepte — Hauptseite mit Sammlungen + Teilen + PDF
   ══════════════════════════════════════════════════════════════ */

export default function AppRezepte() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const [selectedMealType, setSelectedMealType] = useState<MealType>("mittag");
  const [preferences, setPreferences] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [filter, setFilter] = useState<string>("alle");
  const [showManualForm, setShowManualForm] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState<number | null>(null);
  const [activeCollectionId, setActiveCollectionId] = useState<number | null>(null);
  const [showCollections, setShowCollections] = useState(false);

  const { data: allRecipes, isLoading, refetch } = trpc.recipes.getAll.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const { data: collections = [], refetch: refetchCollections } = trpc.recipeCollections.getAll.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  // Load collection items when a collection is selected
  const { data: collectionItems } = trpc.recipeCollections.getItems.useQuery(
    { collectionId: activeCollectionId! },
    { enabled: activeCollectionId !== null && activeCollectionId > 0 }
  );

  const generateMutation = trpc.recipes.generate.useMutation({
    onSuccess: () => { refetch(); toast.success(t("rezepte.neuesRezeptErstellt")); setIsGenerating(false); setPreferences(""); },
    onError: (err) => { toast.error("Fehler: " + err.message); setIsGenerating(false); },
  });

  const favMutation = trpc.recipes.toggleFavorite.useMutation({ onSuccess: () => refetch() });
  const deleteMutation = trpc.recipes.delete.useMutation({ onSuccess: () => { refetch(); toast.success(t("rezepte.rezeptGeloescht")); } });

  const shareMutation = trpc.community.sendMessage.useMutation({
    onSuccess: () => toast.success(t("rezepte.rezeptGeteilt")),
    onError: (err) => toast.error(t("rezepte.fehlerTeilen", { msg: err.message })),
  });

  const handleGenerate = () => {
    if (!client?.clientId) return;
    setIsGenerating(true);
    generateMutation.mutate({ clientId: client.clientId, mealType: selectedMealType, preferences: preferences || undefined });
  };

  const handleShareRecipe = (recipe: typeof filteredRecipes[0]) => {
    if (!client?.clientId) return;
    const text = recipeToText(recipe);
    const senderName = `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() || "AYU-Nutzer";
    shareMutation.mutate({
      roomSlug: "rezepte",
      clientId: client.clientId,
      senderName,
      content: text,
    });
  };

  // Filter recipes based on active collection or meal type filter
  const collectionRecipeIds = useMemo(() => {
    if (!collectionItems) return null;
    return new Set(collectionItems.map((item) => item.recipeId));
  }, [collectionItems]);

  const filteredRecipes = useMemo(() => {
    let recipes = allRecipes ?? [];

    // Filter by collection
    if (activeCollectionId === -1) {
      // Favoriten
      recipes = recipes.filter((r) => r.isFavorite);
    } else if (activeCollectionId !== null && activeCollectionId > 0 && collectionRecipeIds) {
      recipes = recipes.filter((r) => collectionRecipeIds.has(r.id));
    }

    // Filter by meal type (only when viewing "Alle" or "Favoriten")
    if (filter !== "alle" && filter !== "favoriten") {
      recipes = recipes.filter((r) => r.mealType === filter);
    }

    return recipes;
  }, [allRecipes, activeCollectionId, collectionRecipeIds, filter]);

  const editingRecipe = editingRecipeId ? allRecipes?.find((r) => r.id === editingRecipeId) ?? null : null;

  const activeCollection = activeCollectionId && activeCollectionId > 0
    ? collections.find((c) => c.id === activeCollectionId)
    : null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f8f8] p-4 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-6">
          {/* Collections Sidebar (Desktop) */}
          <div className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-4">
              {client?.clientId && (
                <CollectionsSidebar
                  clientId={client.clientId}
                  activeCollectionId={activeCollectionId}
                  onSelectCollection={setActiveCollectionId}
                  collections={collections}
                  refetchCollections={refetchCollections}
                />
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-3">
                <h1 className="text-3xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {activeCollection ? (
                    <span className="flex items-center gap-2">
                      {activeCollection.emoji} {activeCollection.name}
                    </span>
                  ) : activeCollectionId === -1 ? (
                    t("rezepte.favoriten")
                  ) : (
                    t("rezepte.title")
                  )}
                </h1>
                {/* Mobile collections toggle */}
                <button
                  onClick={() => setShowCollections(!showCollections)}
                  className="lg:hidden p-2 rounded-lg hover:bg-[#eef2e6] transition-colors"
                >
                  {showCollections ? <FolderOpen className="w-5 h-5 text-[#6b7f4e]" /> : <Folder className="w-5 h-5 text-[#6b7f4e]" />}
                </button>
              </div>
              <p className="text-[#5a6b4a] text-sm">
                {activeCollection
                  ? activeCollection.description || `${filteredRecipes.length} ${filteredRecipes.length !== 1 ? t("rezepte.rezeptPlural") : t("rezepte.rezeptSingular")}`
                  : t("rezepte.subtitle")}
              </p>
            </div>

            {/* Mobile Collections Panel */}
            {showCollections && client?.clientId && (
              <Card className="lg:hidden bg-white/80 backdrop-blur-sm">
                <CardContent className="py-4">
                  <CollectionsSidebar
                    clientId={client.clientId}
                    activeCollectionId={activeCollectionId}
                    onSelectCollection={(id) => { setActiveCollectionId(id); setShowCollections(false); }}
                    collections={collections}
                    refetchCollections={refetchCollections}
                  />
                </CardContent>
              </Card>
            )}

            {/* Bearbeitungsformular */}
            {editingRecipe && client?.clientId && (
              <EditRecipeForm
                recipe={editingRecipe}
                clientId={client.clientId}
                onSuccess={() => { setEditingRecipeId(null); refetch(); }}
                onCancel={() => setEditingRecipeId(null)}
              />
            )}

            {/* Drei Optionen */}
            {!editingRecipe && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="bg-white/80 backdrop-blur-sm border-[#d4dcc4] hover:shadow-md transition-shadow">
                  <CardContent className="py-4 space-y-4">
                    <h3 className="text-sm font-medium text-[#2d3a1e] flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#6b7f4e]" />{t("rezepte.rezeptGenerieren")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {MEAL_TYPES.map((mt) => (
                        <button key={mt.value} onClick={() => setSelectedMealType(mt.value)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${selectedMealType === mt.value ? "bg-[#6b7f4e] text-white" : "bg-[#eef2e6] text-[#5a6b4a] hover:bg-[#6b7f4e]/20"}`}>
                          {mt.icon}{t(mt.labelKey as any)}
                        </button>
                      ))}
                    </div>
                    <input type="text" value={preferences} onChange={(e) => setPreferences(e.target.value)} placeholder={t("rezepte.wuensche")} className={inputClass} />
                    <Button onClick={handleGenerate} disabled={isGenerating} className="w-full bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white">
                      {isGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("rezepte.wirdErstellt")}</> : <><Sparkles className="w-4 h-4 mr-2" />{t("rezepte.generieren")}</>}
                    </Button>
                  </CardContent>
                </Card>

                {!showManualForm && (
                  <Card className="bg-white/80 backdrop-blur-sm border-[#d4dcc4] hover:shadow-md transition-shadow cursor-pointer group" onClick={() => setShowManualForm(true)}>
                    <CardContent className="py-4 h-full flex flex-col items-center justify-center text-center space-y-3">
                      <div className="w-12 h-12 rounded-full bg-[#6b7f4e]/10 flex items-center justify-center group-hover:bg-[#6b7f4e]/20 transition-colors">
                        <PenLine className="w-6 h-6 text-[#6b7f4e]" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-[#2d3a1e]">{t("rezepte.eigenesRezept")}</h3>
                        <p className="text-xs text-[#8a9680] mt-1">{t("rezepte.eigenesRezeptDesc")}</p>
                      </div>
                      <Button variant="outline" size="sm" className="text-[#6b7f4e] border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/10"
                        onClick={(e) => { e.stopPropagation(); setShowManualForm(true); }}>
                        <Plus className="w-3.5 h-3.5 mr-1.5" />{t("rezepte.hinzufuegen")}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {client?.clientId && <PhotoImportCard clientId={client.clientId} onSuccess={() => refetch()} />}
              </div>
            )}

            {/* Manual Form */}
            {showManualForm && client?.clientId && !editingRecipe && (
              <AddManualRecipeForm clientId={client.clientId} onSuccess={() => { setShowManualForm(false); refetch(); }} onCancel={() => setShowManualForm(false)} />
            )}

            {/* Meal Type Filter */}
            {(allRecipes?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "alle", labelKey: "rezepte.alle_label" as const },
                  ...MEAL_TYPES.map((mt) => ({ value: mt.value, labelKey: mt.labelKey as any })),
                ].map((f) => (
                  <button key={f.value} onClick={() => setFilter(f.value)}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${filter === f.value ? "bg-[#6b7f4e] text-white" : "bg-[#eef2e6] text-[#5a6b4a] hover:bg-[#6b7f4e]/20"}`}>
                    {t(f.labelKey as any)}
                  </button>
                ))}
              </div>
            )}

            {/* Rezepte-Liste */}
            {filteredRecipes.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardContent className="py-12 text-center space-y-3">
                  <ChefHat className="w-12 h-12 text-[#8a9680] mx-auto" />
                  <p className="text-sm text-[#5a6b4a]">
                    {activeCollection
                      ? t("rezepte.nochKeinRezeptCollection", { name: activeCollection.name })
                      : activeCollectionId === -1
                        ? t("rezepte.nochKeinFavoriten")
                        : t("rezepte.nochKeinRezeptHauptseite")}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredRecipes.map((recipe) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    clientId={client?.clientId}
                    collections={collections}
                    onToggleFavorite={() => favMutation.mutate({ recipeId: recipe.id, clientId: client?.clientId ?? "" })}
                    onDelete={() => deleteMutation.mutate({ recipeId: recipe.id, clientId: client?.clientId ?? "" })}
                    onEdit={() => { setEditingRecipeId(recipe.id); setShowManualForm(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    onShare={() => handleShareRecipe(recipe)}
                  />
                ))}
              </div>
            )}

            {/* Counter */}
            {(allRecipes?.length ?? 0) > 0 && (
              <p className="text-center text-[10px] text-[#8a9680]">
                {filteredRecipes.length}{activeCollection || activeCollectionId === -1 ? ` ${t("rezepte.vonAnzahl", { total: String(allRecipes?.length ?? 0) })}` : ""} {filteredRecipes.length !== 1 ? t("rezepte.rezeptPlural") : t("rezepte.rezeptSingular")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
